import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import {
  ItemDisplay,
  LeverageCard,
  LeverageCardRow,
  PercentagePicker,
} from '../fixFlip/leverageComponents';
import { LeverageCardProps } from '../fixFlip/leverageStep';
import { CurrencyField, FieldLabel, NumberFormat, TextField } from '@roc/ui';
import { isNotBlank } from '@roc/feature-utils';
import { makeStyles } from '@material-ui/core/styles';
import { fontWeight } from 'html2canvas/dist/types/css/property-descriptors/font-weight';

const fieldLabelStyle = {
  fontSize: '14px',
  textAlign: 'center',
  width: '100%',
  display: 'block',
  color: '#939494',
  marginBottom: '0px',
  marginTop: '4px',
};

const useStyles = makeStyles({
  fieldValue: {
    '& .MuiInput-input': {
      color: 'rgb(107, 108, 113)',
      fontSize: '20px',
      textAlign: 'center',
      fontWeight: 'bold',
    },
  },
});

export const GroundUpLeverageCard = observer(
  ({ store, disabled, quoteTypeStore }: LeverageCardProps) => {
    const calculated = quoteTypeStore.loanEconomicsStore.calculateFields();
    const ranges = {
      minLoanToCost: 0,
      maxLoanToCost: 100,
      minConstructionReserve: 0,
      maxConstructionReserve: 100,
      maxHighFicoLtc: 90,
    };

    const showEmptyValues = false;
    //Do not remove: observe changes to these fields to update calculations and displayed values on the card
    const initialAdvance = store.form.fields.initialAdvance.value;
    const constructionHoldback = store.form.fields.constructionHoldback.value;

    const handleInitialAdvance = value => {
      store.onFieldChange('initialAdvance', value);
    };

    const handleConstructionHoldback = value => {
      store.onFieldChange('constructionHoldback', value);
    };


    return (
      <LeverageCard title={'Choose Leverage'} disabled={disabled}>
        <LeverageCardRow
          dividend={
            <InitialAdvance store={store} onChange={handleInitialAdvance} />
          }
          divisor={
            <ItemDisplay
              label={
                quoteTypeStore.isMidConstructionRefinance
                  ? 'Cost Spent to Date'
                  : 'Purchase Price'
              }
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalPurchasePrice)
              }
            />
          }
          result={
            <ItemDisplay
              label="Loan to Cost"
              value={showEmptyValues ? '-' : formatPercentage(calculated.loanToCost)}
            />
          }
        />
        <LeverageCardRow
          dividend={
            <ConstructionHoldback
              store={store}
              onChange={handleConstructionHoldback}
            />
          }
          divisor={
            <ItemDisplay
              label="Proposed Construction Budget"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalProposedConstructionBudget)
              }
            />
          }
          result={
            <ItemDisplay
              label="Construction Reserve"
              value={showEmptyValues ? '-' : formatPercentage(calculated.constructionReserve)}
            />
          }
        />
        <LeverageCardRow
          dividend={
            <ItemDisplay
              label="Total Loan Amount"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalLoanAmount)
              }
            />
          }
          divisor={
            <ItemDisplay
              label="Total Cost"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.costBasis)
              }
            />
          }
          result={
            <ItemDisplay
              label="Total Loan To Cost"
              value={
                showEmptyValues
                  ? '-'
                  : formatPercentage(calculated.ltfc)
              }
            />
          }
        />

        <LeverageCardRow
          dividend={
            <ItemDisplay
              label="Total Loan Amount"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalLoanAmount)
              }
            />
          }
          divisor={
            <ItemDisplay
              label="After Repair Value"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalAfterRepairValue)
              }
            />
          }
          result={
            <ItemDisplay
              label="ARLTV"
              value={
                showEmptyValues
                  ? '-'
                  : formatPercentage(calculated.afterRepairLTV)
              }
            />
          }
        />
      </LeverageCard>
    );
  }
);



export const InitialAdvance = observer(({ store, onChange, ...rest }) => {
  const formFields = store.form.fields;
  const classes = useStyles();
  return (
    <>
      <FieldLabel style={fieldLabelStyle}>Initial Advance</FieldLabel>
      <CurrencyField
        inputClassname={classes.fieldValue}
        testId="initialAdvance"
        value={formFields.initialAdvance.value}
        onChange={(e, value) => onChange(value)}
        error={isNotBlank(formFields.initialAdvance.error)}
      />
    </>
  );
});

export const ConstructionHoldback = observer(({ store, onChange, ...rest }) => {
  const formFields = store.form.fields;
  const classes = useStyles();
  return (
    <>
      <FieldLabel style={fieldLabelStyle}>Construction Holdback</FieldLabel>
      <CurrencyField
        standaloneLabel
        inputClassname={classes.fieldValue}
        testId="constructionHoldback"
        value={formFields.constructionHoldback.value}
        onChange={(e, value) => onChange(value)}
        error={isNotBlank(formFields.constructionHoldback.error)}
      />
    </>
  );
});
